import type { ReactNode } from 'react'
import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router'

import {
  getSidebarMainCollapse,
  setSidebarMainCollapse,
} from '../../utils/prefs'
import GlobalFilterBar from '../GlobalFilterBar'

import { onAiAccountEnvFilterPath } from './aiGlobalFilterPaths'
import Header from './Header'
import Sidebar from './Sidebar'

type LayoutProps = {
  children: ReactNode
}

// @purpose App shell wrapping page content with the sidebar, header, and route-aware global filter bar
const Layout = ({ children }: LayoutProps): React.JSX.Element => {
  const [sidebarOpen, setSidebarOpen] = useState(
    () => !getSidebarMainCollapse(), // inverted because getSidebarMainCollapse returns true when collapsed
  )
  const location = useLocation()

  // Load initial sidebar state from preferences
  useEffect(() => {
    const savedCollapsedState = getSidebarMainCollapse()
    setSidebarOpen(!savedCollapsedState) // inverted because state tracks "open" but pref tracks "collapsed"
  }, [])

  const toggleSidebar = () => {
    const newOpenState = !sidebarOpen
    setSidebarOpen(newOpenState)
    // Save the collapsed state (inverted from open state)
    setSidebarMainCollapse(!newOpenState)
  }

  // The AI Applications catalog INDEX supports the global time filter but NOT
  // the account/environment filter, so its bar renders in timeOnly mode (time
  // chip only). The detail route (/ai-applications/catalog/:id) does not consume
  // the filter, so match the index path exactly (tolerating a trailing slash)
  // rather than by prefix. Other AI Applications routes don't consume any global
  // filter yet (follow-up branches).
  const isAiApplicationsCatalogIndex =
    location.pathname === '/ai-applications/catalog' ||
    location.pathname === '/ai-applications/catalog/'
  // All Security Concerns sub-tab routes get the time-only filter bar (prefix
  // match, since /types, /detections, /signals all support it).
  const isAiSecurityConcernsRoute = location.pathname.startsWith(
    '/ai-applications/security-concerns',
  )
  // Shadow AI is a single index route (no sub-tabs) — match it exactly
  // (tolerating a trailing slash) rather than by prefix.
  const isAiShadowAiRoute =
    location.pathname === '/ai-applications/shadow-ai' ||
    location.pathname === '/ai-applications/shadow-ai/'
  // AI Incidents is a single index route — match exactly (tolerating a
  // trailing slash) rather than by prefix.
  const isAiIncidentsRoute =
    location.pathname === '/ai-applications/incidents' ||
    location.pathname === '/ai-applications/incidents/'
  const isTimeOnlyRoute =
    isAiApplicationsCatalogIndex ||
    isAiSecurityConcernsRoute ||
    isAiShadowAiRoute ||
    isAiIncidentsRoute

  // The six in-scope AI Security pages get the FULL chip strip (account +
  // environment + time), not the time-only variant — tech-spec.md §8. AI
  // Identities is one of the six, so it's covered here via the boundary-safe
  // allowlist (which won't leak onto lookalike siblings like /identities-old),
  // matching Header's strict route gating.
  const isFullFilterRoute = onAiAccountEnvFilterPath(location.pathname)

  // Determine if global filter bar should be hidden based on current route.
  // Hide on pages that are NOT /, /risk, /inventory (infrastructure),
  // /security (detection), the AI Applications catalog index, the AI Security
  // Concerns tabs, or one of the six in-scope AI pages.
  const shouldHideGlobalFilterBar =
    location.pathname !== '/' &&
    !location.pathname.startsWith('/risk') &&
    !location.pathname.startsWith('/inventory') &&
    !location.pathname.startsWith('/security') &&
    !isTimeOnlyRoute &&
    !isFullFilterRoute

  return (
    <div className="flex h-screen bg-slate-50 dark:bg-slate-900">
      {/* Sidebar */}
      <Sidebar isOpen={sidebarOpen} onToggle={toggleSidebar} />

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <Header />

        {/* Global Filter Bar - shows active global filters */}
        {!shouldHideGlobalFilterBar && (
          <GlobalFilterBar timeOnly={isTimeOnlyRoute && !isFullFilterRoute} />
        )}

        {/* Main Content Area */}
        <main className="flex-1 p-6 min-h-0">{children}</main>
      </div>
    </div>
  )
}

export default Layout
